import DetailItem from 'types/models/DetailItem';
import Order from 'types/models/Order';
import { CloverServicesAndroidApiServicePrintingProps } from '../../modules/clover-android-services/src/CloverAndroidServices.types';
import { printReceipt } from '../../modules/clover-android-services';
import { isBase64Image } from './images';
import CommerceImageTypes from 'types/enums/CommerceImageTypes';

export const calculateTotalAmount = (detail: DetailItem[]): number => {
  const prices = detail.map((item) => item.product.price * item.quantity);
  const reducer = (accumulator: number, currentValue: number) => accumulator + currentValue;

  return prices.reduce(reducer);
};

export const parseOrderDataToPrintableStringOnCloverDevice = (
  order: Order,
  options?: { eventName: string | null; currentLuxuryBoxDescription: string | null; isReimpression: boolean },
) => {
  const { isReimpression } = options || {};

  const [, month, rest] = order.localDateTime.split('-');
  const day = rest.split(' ')[0];
  const eventName = options?.eventName && options.eventName.trim().length > 0 ? options.eventName : null;
  const currentLuxuryBoxDescription =
    options?.currentLuxuryBoxDescription && options.currentLuxuryBoxDescription.trim().length > 0
      ? options.currentLuxuryBoxDescription
      : null;
  const rawTicketLogo =
    order.commerce.images.find((image) => image.commerceImageTypeId === CommerceImageTypes.TicketLogo)?.path?.trim() ||
    '';

  const ticketLogo = isBase64Image(rawTicketLogo) ? '' : rawTicketLogo;
  const matriculaNumber =
    order.orderDetails?.find((detail) => detail.tomorrowBuyerMatricula)?.tomorrowBuyerMatricula || '';

  let orderDetailData = order.orderDetails?.reduce((acc, currentValue, index, arr) => {
    if (index < arr.length - 1) {
      acc += `${currentValue.product.name}, ${currentValue.quantity}, ${
        currentValue.product.price * currentValue.quantity
      }${
        currentValue.tomorrowQuantityBenefit && currentValue.tomorrowQuantityBenefit > 0
          ? `, ${currentValue.tomorrowQuantityBenefit}, ${currentValue.tomorrowBenefitDiscountPercentage}`
          : ''
      };`;
    } else {
      acc += `${currentValue.product.name}, ${currentValue.quantity}, ${
        currentValue.product.price * currentValue.quantity
      }${
        currentValue.tomorrowQuantityBenefit && currentValue.tomorrowQuantityBenefit > 0
          ? `, ${currentValue.tomorrowQuantityBenefit}, ${currentValue.tomorrowBenefitDiscountPercentage}`
          : ''
      }`;
    }
    return acc;
  }, '');

  // Orden necesario para imprimir ticket
  orderDetailData += `additionalData:`;
  orderDetailData += `matriculaNumber:${matriculaNumber}`;
  orderDetailData += `ticketLogo:${ticketLogo}`;
  orderDetailData += `date:${day}/${month}`;
  orderDetailData += `eventName:${eventName}`;
  orderDetailData += `id:${order.id}`;
  orderDetailData += `isCourtesy:${order.isCourtesy}`;
  orderDetailData += `isReimpression:${Boolean(isReimpression)}`;
  orderDetailData += `currentLuxuryBoxDescription:${currentLuxuryBoxDescription}`;
  orderDetailData += `commerceName:${order.commerce.name}`;
  orderDetailData += `totalAmount:${order.totalAmount}`;

  if (order.delivery) {
    orderDetailData += `delivery:${order.delivery}`;
  }
  if (order.assignedPickupVerificationMethod) {
    const verificationCodeUrl = order.images?.find(
      (image) => image.orderImageTypeId === order.assignedPickupVerificationMethod,
    )?.path;
    if (verificationCodeUrl) {
      orderDetailData += `verificationCodeUrl:${verificationCodeUrl}`;
    }
  }
  return orderDetailData;
};

export const setUpPayloadForPrintingOnCloverDevice = ({
  orderToPrintableString,
  isCloverFlex,
  isFromSitefApp,
  verificationMethodAssigned,
  paymentMethod,
  isRefund,
  discountPercentage,
  paymentWasSubsidized = undefined,
}: CloverServicesAndroidApiServicePrintingProps): CloverServicesAndroidApiServicePrintingProps => {
  const receiptPayload: CloverServicesAndroidApiServicePrintingProps = {
    orderToPrintableString,
    isCloverFlex,
    isFromSitefApp,
    paymentMethod,
    isRefund,
    discountPercentage,
    paymentWasSubsidized,
    verificationMethodAssigned,
  };

  return receiptPayload;
};

export const handleTicketsImpression = async ({
  order,
  isPortraitScreen,
  isFromSitefApp,
  paymentMethod,
  impressOneTicket = false,
  eventName = null,
  currentLuxuryBoxDescription = null,
  isReimpression = false,
}: {
  order: Order;
  isPortraitScreen: boolean;
  isFromSitefApp: boolean;
  paymentMethod: string | undefined;
  impressOneTicket?: boolean;
  eventName?: string | null;
  currentLuxuryBoxDescription?: string | null;
  isReimpression?: boolean;
}): Promise<boolean> => {
  console.log('Boolean(order.orderDetails)', Boolean(order.orderDetails));
  if (!order.orderDetails) return false;

  if (impressOneTicket) {
    const orderString = parseOrderDataToPrintableStringOnCloverDevice(order, {
      eventName,
      currentLuxuryBoxDescription,
      isReimpression,
    });
    if (!orderString) return false;

    const ticketPayload = setUpPayloadForPrintingOnCloverDevice({
      orderToPrintableString: orderString,
      isCloverFlex: isPortraitScreen,
      isFromSitefApp,
      paymentMethod,
      verificationMethodAssigned: order.assignedPickupVerificationMethod,
      discountPercentage: order.discountPercentage,
      paymentWasSubsidized: Boolean(order.orderDetails?.some((d) => d.tomorrowQuantityBenefit)),
    });

    return await printReceipt(ticketPayload);
  }

  const ordersBR: CloverServicesAndroidApiServicePrintingProps[] = [];

  for (const detail of order.orderDetails) {
    const price = Number(detail.price);
    const benefitQuantity = Number(detail.tomorrowQuantityBenefit || 0);
    const discountPercentage = Number(detail.tomorrowBenefitDiscountPercentage || 0);

    for (let i = 0; i < detail.quantity; i++) {
      const isBenefitUnit = i < benefitQuantity; // Solo las primeras N unidades tienen descuento
      const totalAmount = order.isCourtesy
        ? 0
        : Number((isBenefitUnit ? price * (1 - discountPercentage / 100) : price).toFixed(2));

      const detailCopy = {
        ...detail,
        quantity: 1,
        tomorrowQuantityBenefit: isBenefitUnit ? 1 : 0,
      };

      const images = order.images?.filter((img) => img.orderDetailId === detailCopy.id);
      const newOrder: Order = { ...order, orderDetails: [detailCopy], totalAmount, images };

      const orderString = parseOrderDataToPrintableStringOnCloverDevice(newOrder, {
        eventName,
        currentLuxuryBoxDescription,
        isReimpression,
      });
      if (!orderString) return false;

      const ticketPayload = setUpPayloadForPrintingOnCloverDevice({
        orderToPrintableString: orderString,
        isCloverFlex: isPortraitScreen,
        isFromSitefApp,
        paymentMethod,
        verificationMethodAssigned: newOrder.assignedPickupVerificationMethod,
        discountPercentage,
        paymentWasSubsidized: isBenefitUnit,
      });

      ordersBR.push(ticketPayload);
    }
  }

  // TODO
  return false;
};

export const handleUniqueProductImpression = async ({
  order,
  productId,
  isPortraitScreen,
  paymentMethod,
  eventName = null,
  isFromSitefApp,
  isReimpression = false,
}: {
  order: Order;
  productId: string;
  isPortraitScreen: boolean;
  paymentMethod: string | undefined;
  isFromSitefApp: boolean;
  eventName?: string | null;
  isReimpression?: boolean;
}): Promise<boolean> => {
  const detail = order.orderDetails?.find((detail) => detail.product.id === productId);
  if (!detail) {
    console.log(JSON.stringify(order.orderDetails), 'yyyy', productId, '---');
    return false;
  }
  const price = Number(detail.price);
  const totalAmount = order.isCourtesy ? 0 : Number(price.toFixed(2));
  const detailCopy = {
    ...detail,
    quantity: 1,
  };

  const images = order.images?.filter((img) => img.orderDetailId === detailCopy.id);
  const newOrder: Order = { ...order, orderDetails: [detailCopy], totalAmount, images };

  const orderString = parseOrderDataToPrintableStringOnCloverDevice(newOrder, {
    eventName,
    currentLuxuryBoxDescription: null,
    isReimpression,
  });
  if (!orderString) return false;

  const ticketPayload = setUpPayloadForPrintingOnCloverDevice({
    orderToPrintableString: orderString,
    isCloverFlex: isPortraitScreen,
    isFromSitefApp,
    verificationMethodAssigned: newOrder.assignedPickupVerificationMethod,
    paymentMethod,
  });

  return await printReceipt(ticketPayload);
};
